/**
 * Parse a markdown/text block into a SchemaAnnotationsDelta.
 *
 * Target format (loose — we try to be forgiving):
 *
 *     Free-form intro paragraphs...
 *     _____________________________________________________________
 *
 *     club_dimension
 *     Tabell
 *     •    club_dimension: One-line description of the table.
 *     Kolumner
 *     •    clubId [numeriskt]: One-line description of the column.
 *     •    clubName [text]: Another column.
 *
 *     (Optional trailing prose is appended to the table description.)
 *     _____________________________________________________________
 *
 *     (next table block...)
 *
 * Everything before the first divider becomes the connection `summary`.
 * Each block between dividers is parsed as one table with its description
 * and column annotations.
 *
 * When a table name or column name doesn't exist in the live catalog,
 * we report it as "unmatched" so the UI can warn before PATCH — the
 * backend rejects unknown names silently anyway, but surfacing them
 * lets the admin fix typos.
 */

import type { DataConnectionSchemaStatus, SchemaAnnotationsDelta } from "@/api/data-connections-api"

// Bullet chars: • (U+2022), *, -, –, —
// We accept leading whitespace + any of these + whitespace.
const BULLET_RE = /^\s*[•*\-–—]\s+/

// Section headers (case-insensitive, matches Swedish + English)
const TABLE_HEADER_RE = /^\s*(Tabell|Table)\s*:?\s*$/i
const COLUMNS_HEADER_RE = /^\s*(Kolumner|Columns|Fält|Fields)\s*:?\s*$/i

// Divider line: 5 or more underscores (with optional whitespace)
const DIVIDER_RE = /^\s*_{5,}\s*$/

// Column line format:  name [type]: description   OR   name: description
// The type bracket is optional. We keep the colon-delimited tail as description.
const COLUMN_LINE_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*(?:\[[^\]]*\])?\s*[:：]\s*(.+?)\s*$/

// Table description bullet:  table_name: description
const TABLE_DESC_BULLET_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*[:：]\s*(.+?)\s*$/

export type ParsedTable = {
  tableName: string
  description?: string
  columns: Record<string, string>
}

export type ParsedAnnotations = {
  summary?: string
  tables: ParsedTable[]
}

export type MatchReport = {
  /** Tables that matched an existing schema.table in the catalog. */
  matchedTables: Array<{
    parsed: ParsedTable
    schema: string
    table: string
    matchedColumns: string[]
    unmatchedColumns: string[]
  }>
  /** Parsed tables that didn't resolve to any catalog entry. */
  unmatchedTables: ParsedTable[]
  /** Parsed summary, applied at connection level. */
  summary?: string
}

/** Pure text parsing — no catalog knowledge. */
export function parseAnnotationsText(text: string): ParsedAnnotations {
  const normalised = text.replace(/\r\n/g, "\n")
  const lines = normalised.split("\n")

  // Split into "blocks" on divider lines. First block is summary, rest are tables.
  const blocks: string[][] = [[]]
  for (const line of lines) {
    if (DIVIDER_RE.test(line)) {
      if (blocks[blocks.length - 1].length > 0) blocks.push([])
    } else {
      blocks[blocks.length - 1].push(line)
    }
  }

  const summary = blocks[0] ? cleanParagraph(blocks[0].join("\n")) : undefined

  const tables: ParsedTable[] = []
  for (const block of blocks.slice(1)) {
    const parsed = parseTableBlock(block)
    if (parsed) tables.push(parsed)
  }

  return {
    summary: summary || undefined,
    tables,
  }
}

function parseTableBlock(lines: string[]): ParsedTable | null {
  // Strip blank lines from the top
  let i = 0
  while (i < lines.length && lines[i].trim() === "") i++
  if (i >= lines.length) return null

  // First non-empty line is the table name (heading).
  const headerLine = lines[i].trim()
  const tableName = extractIdentifier(headerLine)
  if (!tableName) return null
  i++

  let description: string | undefined
  const columns: Record<string, string> = {}
  let mode: "pre" | "table" | "columns" = "pre"

  for (; i < lines.length; i++) {
    const line = lines[i]
    if (line.trim() === "") continue
    if (TABLE_HEADER_RE.test(line)) {
      mode = "table"
      continue
    }
    if (COLUMNS_HEADER_RE.test(line)) {
      mode = "columns"
      continue
    }

    const bullet = BULLET_RE.test(line) ? line.replace(BULLET_RE, "") : null

    if (mode === "table" && bullet) {
      const match = bullet.match(TABLE_DESC_BULLET_RE)
      if (match) {
        // Pattern "table_name: description" — take description (2nd group).
        description = description ? `${description} ${match[2]}` : match[2]
      } else {
        // No name: prefix; treat whole bullet as description.
        const text = bullet.trim()
        description = description ? `${description} ${text}` : text
      }
      continue
    }

    if (mode === "columns" && bullet) {
      const match = bullet.match(COLUMN_LINE_RE)
      if (match) {
        const [, colName, colDesc] = match
        // First-write-wins on dup names in source text — they're usually
        // accidental; later occurrences often drift.
        if (!columns[colName]) columns[colName] = colDesc.trim()
      }
      continue
    }

    // Parenthetical trailing notes land on the table description no
    // matter which mode we're in — admins commonly append them after
    // the columns block ("Tabellen innehåller även rader för...").
    const text = line.trim()
    if (text.startsWith("(") || text.startsWith("[")) {
      description = description ? `${description} ${text}` : text
    }
  }

  return {
    tableName,
    description: description?.trim() || undefined,
    columns,
  }
}

function extractIdentifier(line: string): string | null {
  // Strip leading markdown heading markers (# ## etc.) and stray
  // markdown emphasis chars. Do NOT strip underscores — they're part
  // of most SQL identifiers (club_dimension, purchase_fact).
  const cleaned = line
    .replace(/^#+\s*/, "")
    .replace(/[*`]/g, "")
    .trim()
  const match = cleaned.match(/^([A-Za-z_][A-Za-z0-9_.]*)\b/)
  return match ? match[1] : null
}

function cleanParagraph(text: string): string {
  return text.trim().replace(/\n{3,}/g, "\n\n")
}

/**
 * Match parsed tables against a live catalog. Auto-picks the schema for
 * each table name; when a table name exists in multiple schemas we prefer
 * the one with the most column overlaps (ties → first found).
 */
export function matchAgainstCatalog(
  parsed: ParsedAnnotations,
  catalog: DataConnectionSchemaStatus | null | undefined
): MatchReport {
  const schemas = catalog?.schemas ?? {}
  const matched: MatchReport["matchedTables"] = []
  const unmatched: ParsedTable[] = []

  for (const parsedTable of parsed.tables) {
    const candidates: Array<{
      schema: string
      columnOverlap: number
      columns: string[]
    }> = []

    for (const [schemaName, schemaEntry] of Object.entries(schemas)) {
      const tables = schemaEntry.tables ?? {}
      if (!Object.hasOwn(tables, parsedTable.tableName)) continue
      const tableEntry = tables[parsedTable.tableName]
      const columnNames = new Set((tableEntry.columns ?? []).map((c) => c.name))
      const overlap = Object.keys(parsedTable.columns).filter((n) => columnNames.has(n))
      candidates.push({
        schema: schemaName,
        columnOverlap: overlap.length,
        columns: [...columnNames],
      })
    }

    if (candidates.length === 0) {
      unmatched.push(parsedTable)
      continue
    }

    // Prefer schema with highest column overlap.
    candidates.sort((a, b) => b.columnOverlap - a.columnOverlap)
    const best = candidates[0]

    // Ambiguity guard: when more than one schema owns the table name
    // AND our best candidate has zero column overlap, we can't pick
    // confidently. Route the whole table to "unmatched" so the admin
    // qualifies the source text (e.g. adds a schema prefix) rather
    // than silently landing descriptions on the wrong schema.
    if (candidates.length > 1 && best.columnOverlap === 0) {
      unmatched.push(parsedTable)
      continue
    }

    const columnSet = new Set(best.columns)
    const matchedCols: string[] = []
    const unmatchedCols: string[] = []
    for (const name of Object.keys(parsedTable.columns)) {
      if (columnSet.has(name)) matchedCols.push(name)
      else unmatchedCols.push(name)
    }

    matched.push({
      parsed: parsedTable,
      schema: best.schema,
      table: parsedTable.tableName,
      matchedColumns: matchedCols,
      unmatchedColumns: unmatchedCols,
    })
  }

  return {
    matchedTables: matched,
    unmatchedTables: unmatched,
    summary: parsed.summary,
  }
}

/** Convert a match report to the sparse delta the PATCH endpoint accepts. */
export function matchReportToDelta(report: MatchReport): SchemaAnnotationsDelta {
  const delta: SchemaAnnotationsDelta = {}
  if (report.summary !== undefined) delta.summary = report.summary

  const schemas: NonNullable<SchemaAnnotationsDelta["schemas"]> = {}
  for (const entry of report.matchedTables) {
    const schemaBucket = schemas[entry.schema] ?? (schemas[entry.schema] = {})
    const tablesBucket = schemaBucket.tables ?? (schemaBucket.tables = {})
    const tableBucket: NonNullable<
      NonNullable<SchemaAnnotationsDelta["schemas"]>[string]["tables"]
    >[string] = {}

    if (entry.parsed.description !== undefined) {
      tableBucket.description = entry.parsed.description
    }

    const cols: Record<string, { description?: string | null }> = {}
    for (const colName of entry.matchedColumns) {
      const desc = entry.parsed.columns[colName]
      if (desc) cols[colName] = { description: desc }
    }
    if (Object.keys(cols).length > 0) tableBucket.columns = cols

    tablesBucket[entry.table] = tableBucket
  }

  if (Object.keys(schemas).length > 0) delta.schemas = schemas
  return delta
}
